import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import React from 'react';
import { getFixedNumber } from 'utils/general';

import { ISettlements } from '@/modules/Settlements/types';
import { Typography } from '@/shared/components/atoms';
import { EMBTY_PLACE_HOLDER } from '@/shared/constant/general';
import { IGeneral } from '@/shared/types';

const embtyInstanceItem: ISettlements.ISettlementDetailsDayInstance = {
  payUnit: EMBTY_PLACE_HOLDER,
  suppAssPrice: EMBTY_PLACE_HOLDER,
  supplierAssignmentStatus: 2,
  supplierId: EMBTY_PLACE_HOLDER,
  jobInstanceId: EMBTY_PLACE_HOLDER,
  category: EMBTY_PLACE_HOLDER,
  place: EMBTY_PLACE_HOLDER,
  supervisorName: EMBTY_PLACE_HOLDER,
  jobInstanceName: EMBTY_PLACE_HOLDER,
  assignmentJobName: EMBTY_PLACE_HOLDER,
  cropName: EMBTY_PLACE_HOLDER,
  supplierAssignmentId: EMBTY_PLACE_HOLDER,
  pricingMethod: EMBTY_PLACE_HOLDER,
  unitType: EMBTY_PLACE_HOLDER,
  totalWorkers: EMBTY_PLACE_HOLDER,
  totalTimeSpent: EMBTY_PLACE_HOLDER,
  totalUnitsAchieved: EMBTY_PLACE_HOLDER,
  expectedProductivityTarget: EMBTY_PLACE_HOLDER,
  supervisionBonuses: EMBTY_PLACE_HOLDER,
  supervisionDeduction: EMBTY_PLACE_HOLDER,
  numberHoursBonuses: EMBTY_PLACE_HOLDER,
  totalHoursBonuses: EMBTY_PLACE_HOLDER,
  overTimeDetails: EMBTY_PLACE_HOLDER,
  averagePayUnitsPerWorker: EMBTY_PLACE_HOLDER,
  totalPayUnits: EMBTY_PLACE_HOLDER,
  netPayment: EMBTY_PLACE_HOLDER,
  uploadDate: EMBTY_PLACE_HOLDER,
};
export const getSerializedPrintedData = ({
  days,
  t,
}: {
  t: TFunction<'common', undefined>;
  days: ISettlements.ISettlementDetailsDay[];
}): ISettlements.ISettlementDetailsDayInstance[] => {
  // inject in every day-> date in first instance and supplier deduction in last instance
  const serializedData = [];
  for (let i = 0; i < days?.length; i++) {
    const newDayInstances = [...days[i].instances];

    for (let k = 0; k < newDayInstances?.length; k++) {
      newDayInstances[k] = { ...newDayInstances[k], date: days[i].day };
    }

    if (days[i].totalAdministrativeDeduction)
      newDayInstances.push({
        ...embtyInstanceItem,
        date: t('managerialDeduction'),
        netPayment: days[i]?.totalAdministrativeDeduction || 0,
      });
    if (days[i].totalAdministrativeBonuses)
      newDayInstances.push({
        ...embtyInstanceItem,
        date: t('administrativeIncentives'),
        netPayment: days[i]?.totalAdministrativeBonuses || 0,
      });
    // add indicator to show that is the last instance in the date
    newDayInstances[newDayInstances.length - 1] = {
      ...newDayInstances[newDayInstances.length - 1],
      lastInstanceInDate: true,
    };

    serializedData.push(newDayInstances);
  }

  return serializedData.flat();
};

const getRenderedCellValue = (value: string | number | undefined | null) => (
  <Typography variant='minText' className=' max-w-[9.25rem] '>
    {/* check if number and float make fixed 2 else show value oring with (-) */}
    {typeof value == 'number'
      ? Number.isInteger(value)
        ? value
        : value?.toFixed(2)
      : value === EMBTY_PLACE_HOLDER
      ? ''
      : value || '-'}
  </Typography>
);
const getRenderedHeader = (label: string) => (
  <Typography variant='minText' className=' max-w-[9.25rem] '>
    {label}
  </Typography>
);

export const settlementsPrintColumns = (
  t: TFunction<'common', undefined>,
  currency: string,
  settlementLevel?: IGeneral.ISettlementLevel
) => {
  const columnHelper = createColumnHelper<
    | ISettlements.IWorkerSettlementDaySchema
    | ISettlements.ISettlementDetailsDayInstance
  >();

  const column = [];
  column.push(
    columnHelper.accessor('date', {
      cell: ({
        row: {
          original: { date },
        },
      }) =>
        getRenderedCellValue(
          settlementLevel == 2 ? format(new Date(date), 'dd MMM yyyy') : date
        ),
      header: () => getRenderedHeader(t('date')),
    })
  );
  if (settlementLevel === 2) {
    column.push(
      columnHelper.accessor('totalHours', {
        cell: ({ getValue }) => getValue(),
        header: () => getRenderedHeader(t('total-hours')),
      })
    );
    column.push(
      columnHelper.accessor('totalOperations', {
        cell: ({ getValue }) => getValue(),
        header: () => getRenderedHeader(t('total-operations')),
      })
    );
    column.push(
      columnHelper.accessor('basePay', {
        cell: ({ getValue }) => getValue(),
        header: () => getRenderedHeader(`${t('base-pay')} (${currency})`),
      })
    );
    column.push(
      columnHelper.accessor('totalBonuses', {
        cell: ({ getValue }) => getValue(),
        header: () => getRenderedHeader(`${t('bonus')} (${currency})`),
      })
    );
    column.push(
      columnHelper.accessor('totalDeductions', {
        cell: ({ getValue }) => getValue(),
        header: () => getRenderedHeader(`${t('deduction')} (${currency})`),
      })
    );
    column.push(
      columnHelper.accessor('totalOvertime', {
        cell: ({ getValue }) => getValue(),
        header: () => getRenderedHeader(`${t('OVERTIME')} (${currency})`),
      })
    );
    column.push(
      columnHelper.accessor('totalPay', {
        cell: ({ getValue }) => getValue(),
        header: () => getRenderedHeader(`${t('total-pay')} (${currency})`),
      })
    );
  } else {
    column.push(
      columnHelper.accessor('jobInstanceId', {
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
        header: () => getRenderedHeader(t('job-order')),
      })
    );
    column.push(
      columnHelper.accessor('totalWorkers', {
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
        header: () => getRenderedHeader(t('attendance')),
      })
    );
    column.push(
      columnHelper.accessor('place', {
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
        header: () => getRenderedHeader(t('location')),
      })
    );
    column.push(
      columnHelper.accessor('jobInstanceName', {
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
        header: () => getRenderedHeader(t('executed-job')),
      })
    );
    column.push(
      columnHelper.accessor('payUnit', {
        cell: ({ getValue }) =>
          getRenderedCellValue(
            getValue() === 'daily' ? t('daily') : getValue()
          ),
        header: () => getRenderedHeader(t('pay-unit')),
      })
    );
    column.push(
      columnHelper.accessor('suppAssPrice', {
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
        header: () => getRenderedHeader(`${t('pay-unit-value')} (${currency})`),
      })
    );
    column.push(
      columnHelper.accessor('totalUnitsAchieved', {
        cell: ({ getValue, row: { original } }) =>
          getValue() === EMBTY_PLACE_HOLDER ? (
            ''
          ) : (
            <>
              {getRenderedCellValue(getValue())}{' '}
              <span className='text-10'>
                {'unitType' in original ? original.unitType : ''}
              </span>
            </>
          ),
        header: () => getRenderedHeader(t('actual-productivity-per-team')),
      })
    );
    column.push(
      columnHelper.accessor('supervisionDeduction', {
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
        header: () =>
          getRenderedHeader(`${t('supervisionDeduction')} (${currency})`),
      })
    );
    column.push(
      columnHelper.accessor('supervisionBonuses', {
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
        header: () =>
          getRenderedHeader(`${t('supervisionBonuses')} (${currency})`),
      })
    );
    column.push(
      columnHelper.accessor('totalHoursBonuses', {
        header: () =>
          getRenderedHeader(`${t('value-extra-hours')} (${currency})`),
        // cell: ({ getValue }) => <Typography variant='subtitle2'   className=' overflow-clip text-ellipsis whitespace-nowrap'>{getRenderedCellValue(getValue())}</Typography>,
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
      })
    );

    column.push(
      columnHelper.accessor('averagePayUnitsPerWorker', {
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
        header: () => getRenderedHeader(t('average-pay-unit-per-worker')),
      })
    );
    column.push(
      columnHelper.accessor('totalPayUnits', {
        cell: ({ getValue }) => getRenderedCellValue(getValue()),
        header: () => getRenderedHeader(t('total-number-pay-units')),
      })
    );

    column.push(
      columnHelper.accessor('netPayment', {
        cell: ({ getValue }) => (
          <Typography variant='subtitle2' weight='bold'>
            {getValue() === EMBTY_PLACE_HOLDER
              ? ''
              : getFixedNumber(getValue())}
          </Typography>
        ),

        header: () => getRenderedHeader(`${t('total-dues')} (${currency})`),
      })
    );
  }
  return column;
};
